import { useState, useEffect, useCallback, useRef } from 'react';
import { monitoringWebSocket } from '../services/websocketService';
import { Alert, WaterQuality, SafetyStatus, Cctv, Wearable } from '../types/domain';

// WebSocket 연결 상태를 위한 훅
export function useWebSocketConnectionStatus() {
  const [connectionStatus, setConnectionStatus] = useState<'disconnected' | 'connecting' | 'connected'>(
    monitoringWebSocket.getConnectionStatus()
  );

  useEffect(() => {
    // 연결 상태 변경 구독
    monitoringWebSocket.subscribeToConnectionStatus(setConnectionStatus);

    // 정리 함수
    return () => {
      monitoringWebSocket.unsubscribeFromConnectionStatus(setConnectionStatus);
    };
  }, []);

  return {
    connectionStatus,
    isConnected: connectionStatus === 'connected',
    isConnecting: connectionStatus === 'connecting',
    isDisconnected: connectionStatus === 'disconnected',
  };
}

// 주요 데이터 유형을 위한 고차 함수 사용
export function useWebSocketData<T>(
  subscribeMethod: (callback: (data: T) => void) => void,
  unsubscribeMethod: (callback: (data: T) => void) => void,
  fetchMethod?: () => boolean,
  initialState: T | null = null,
  poolId?: string | number
) {
  const [data, setData] = useState<T | null>(initialState);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { connectionStatus } = useWebSocketConnectionStatus();
  
  // 자동 재시도 관련 상태
  const retryCountRef = useRef(0);
  const maxRetries = 3;
  const retryTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // 데이터 수신 콜백
  const handleData = useCallback((newData: T) => {
    setData(newData);
    setLastUpdated(new Date());
    setIsLoading(false);
    setError(null); // 데이터를 받으면 에러 상태 초기화
    retryCountRef.current = 0; // 성공적으로 데이터를 받으면 재시도 카운터 초기화
  }, []);

  // 데이터 요청 함수
  const fetchData = useCallback(() => {
    if (fetchMethod) {
      setIsLoading(true);
      // 요청 실패 시 에러 처리 및 재시도 로직
      if (!fetchMethod()) {
        console.warn('Failed to request data. Connection may be unavailable.');
        if (retryCountRef.current < maxRetries) {
          // 재시도 타임아웃 설정 (지수 백오프)
          const delay = Math.min(1000 * Math.pow(2, retryCountRef.current), 10000);
          console.log(`Will retry data fetch in ${delay}ms (attempt ${retryCountRef.current + 1}/${maxRetries})`);
          
          // 기존 타임아웃이 있다면 클리어
          if (retryTimeoutRef.current) {
            clearTimeout(retryTimeoutRef.current);
          }
          
          retryTimeoutRef.current = setTimeout(() => {
            retryCountRef.current++;
            fetchData();
          }, delay);
        } else {
          setError('데이터 요청 실패. 나중에 다시 시도');
          setIsLoading(false);
        }
      }
    }
  }, [fetchMethod]);

  // 연결 상태 변경 시 자동 데이터 요청
  useEffect(() => {
    // 연결됐을 때 자동으로 데이터 요청
    if (connectionStatus === 'connected' && fetchMethod) {
      console.log('Connection established. Automatically requesting data.');
      fetchData();
    }
  }, [connectionStatus, fetchData, fetchMethod]);

  // 구독 설정
  useEffect(() => {
    // 구독 설정
    subscribeMethod(handleData);
    
    // 초기 데이터 요청 (poolId가 있고 fetchMethod가 있는 경우)
    if (poolId && fetchMethod) {
      fetchData();
    }
    
    // 컴포넌트 언마운트 시 정리
    return () => {
      unsubscribeMethod(handleData);
      // 재시도 타임아웃 정리
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }
    };
  }, [handleData, subscribeMethod, unsubscribeMethod, poolId, fetchData, fetchMethod]);

  // 수동 데이터 새로고침 함수
  const refresh = useCallback(() => {
    setIsLoading(true);
    setError(null); // 에러 상태 초기화
    retryCountRef.current = 0; // 재시도 카운터 초기화
    
    // 요청 메서드가 있으면 데이터 요청
    if (fetchMethod) {
      fetchData();
    }
  }, [fetchData, fetchMethod]);

  return {
    data,
    isLoading,
    lastUpdated,
    error,
    refresh,
    connectionStatus
  };
}

// 알림 데이터를 위한 특화된 훅
export function useAlertData(poolId?: string | number) {
  const fetchMethod = poolId ? 
    () => monitoringWebSocket.fetchData('alerts', poolId) : 
    undefined;
    
  return useWebSocketData<Alert>(
    monitoringWebSocket.subscribeToAlerts.bind(monitoringWebSocket),
    monitoringWebSocket.unsubscribeFromAlerts.bind(monitoringWebSocket),
    fetchMethod,
    null,
    poolId
  );
}

// 수질 데이터를 위한 특화된 훅
export function useWaterQualityData(poolId?: string | number) {
  const fetchMethod = poolId ? 
    () => monitoringWebSocket.fetchData('water_quality', poolId) : 
    undefined;
    
  return useWebSocketData<WaterQuality>(
    monitoringWebSocket.subscribeToWaterQuality.bind(monitoringWebSocket),
    monitoringWebSocket.unsubscribeFromWaterQuality.bind(monitoringWebSocket),
    fetchMethod,
    null,
    poolId
  );
}

// 안전 상태 데이터를 위한 특화된 훅
export function useSafetyStatusData(poolId?: string | number) {
  const fetchMethod = poolId ? 
    () => monitoringWebSocket.fetchData('safety_status', poolId) : 
    undefined;
    
  return useWebSocketData<SafetyStatus>(
    monitoringWebSocket.subscribeToSafetyStatus.bind(monitoringWebSocket),
    monitoringWebSocket.unsubscribeFromSafetyStatus.bind(monitoringWebSocket),
    fetchMethod,
    null,
    poolId
  );
}

// CCTV 상태 데이터를 위한 특화된 훅
export function useCctvStatusData(poolId?: string | number) {
  const fetchMethod = poolId ? 
    () => monitoringWebSocket.fetchData('cctv_status', poolId) : 
    undefined;
    
  return useWebSocketData<Cctv>(
    monitoringWebSocket.subscribeToCctvStatus.bind(monitoringWebSocket),
    monitoringWebSocket.unsubscribeFromCctvStatus.bind(monitoringWebSocket),
    fetchMethod,
    null,
    poolId
  );
}

// 웨어러블 장치 데이터를 위한 특화된 훅
export function useWearableStatusData(poolId?: string | number) {
  const fetchMethod = poolId ? 
    () => monitoringWebSocket.fetchData('wearable_status', poolId) : 
    undefined;
    
  return useWebSocketData<Wearable>(
    monitoringWebSocket.subscribeToWearableStatus.bind(monitoringWebSocket),
    monitoringWebSocket.unsubscribeFromWearableStatus.bind(monitoringWebSocket),
    fetchMethod,
    null,
    poolId
  );
}

// 특정 풀의 모든 데이터를 한 번에 요청하는 훅
export function usePoolAllData(poolId?: string | number) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastRefreshed, setLastRefreshed] = useState<Date | null>(null);
  const { connectionStatus } = useWebSocketConnectionStatus();

  const fetchAllData = useCallback(() => {
    if (!poolId) {
      // poolId가 없는 화면에서도 훅 호출 순서를 고정하기 위해 조용히 종료
      return false;
    }
    
    setIsLoading(true);
    setError(null);
    
    const success = monitoringWebSocket.fetchAllPoolData(poolId);
    if (success) {
      setLastRefreshed(new Date());
    } else {
      setError('데이터 요청 실패. 연결 상태를 확인');
    }
    
    setIsLoading(false);
    return success;
  }, [poolId]);

  // 연결 상태 변경 시 자동으로 모든 데이터 요청
  useEffect(() => {
    if (connectionStatus === 'connected' && poolId) {
      console.log(`Connection established. Automatically requesting all data for pool ${poolId}`);
      fetchAllData();
    }
  }, [connectionStatus, poolId, fetchAllData]);

  return {
    fetchAllData,
    isLoading,
    error,
    lastRefreshed,
    connectionStatus
  };
}

// 모든 유형의 데이터를 한 번에 관리하는 훅
export function useAllMonitoringData(poolId?: string | number) {
  const alerts = useAlertData(poolId);
  const waterQuality = useWaterQualityData(poolId);
  const safetyStatus = useSafetyStatusData(poolId);
  const cctvStatus = useCctvStatusData(poolId);
  const wearableStatus = useWearableStatusData(poolId);
  const { connectionStatus } = useWebSocketConnectionStatus();
  
  const isAnyLoading = 
    alerts.isLoading || 
    waterQuality.isLoading || 
    safetyStatus.isLoading || 
    cctvStatus.isLoading || 
    wearableStatus.isLoading;
  
  const hasAnyError = 
    alerts.error || 
    waterQuality.error || 
    safetyStatus.error || 
    cctvStatus.error || 
    wearableStatus.error;
  
  const refreshAll = useCallback(() => {
    alerts.refresh();
    waterQuality.refresh();
    safetyStatus.refresh();
    cctvStatus.refresh();
    wearableStatus.refresh();
  }, [
    alerts.refresh,
    waterQuality.refresh,
    safetyStatus.refresh,
    cctvStatus.refresh,
    wearableStatus.refresh
  ]);
  
  // 훅 호출 순서 고정을 위해 항상 호출
  const allPoolData = usePoolAllData(poolId);
  
  return {
    alerts: alerts.data,
    waterQuality: waterQuality.data,
    safetyStatus: safetyStatus.data,
    cctvStatus: cctvStatus.data,
    wearableStatus: wearableStatus.data,
    isLoading: isAnyLoading,
    error: hasAnyError,
    refreshAll,
    connectionStatus,
    fetchAllPoolData: poolId ? allPoolData.fetchAllData : undefined
  };
} 